/**
 * RastreioPublico.jsx
 *
 * Página pública de rastreio em tempo real — estilo Uber Black.
 * Rota: /rastreio/:token
 *
 * ✅ Sem autenticação — rota completamente pública
 * ✅ Leaflet + Mapbox para mapa premium
 * ✅ Socket.io para atualizações em tempo real
 * ✅ Polling a cada 8s como fallback garantido
 * ✅ Animação de motorista pulsante
 * ✅ ETA calculado via Haversine
 * ✅ Bottom sheet estilo app nativo
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { io } from 'socket.io-client';
import 'leaflet/dist/leaflet.css';
import './RastreioPublico.css';

// ─── Config ──────────────────────────────────────────────────────────────────
const MAPBOX_TOKEN =
  'pk.eyJ1IjoiYmxuYXNjaW1lbnRvMjQwMjk4IiwiYSI6ImNtZ2lnYnc1NTA5MmEycG9pM2M5M3IwcWMifQ.JcuK1f9M-yZdwCeTXDx20Q';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const SOCKET_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;

// ─── Ícone custom do motorista ────────────────────────────────────────────────
const createDriverIcon = (color = '#F59E0B') =>
  L.divIcon({
    className: 'rp-driver-icon',
    html: `
      <div class="rp-driver-wrapper">
        <div class="rp-pulse-outer" style="border-color:${color}33"></div>
        <div class="rp-pulse-inner" style="border-color:${color}66"></div>
        <div class="rp-driver-pin" style="background:${color}">
          <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
            <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
          </svg>
        </div>
      </div>`,
    iconSize: [56, 56],
    iconAnchor: [28, 28],
  });

// ─── Ícone do destino ─────────────────────────────────────────────────────────
const DEST_ICON = L.divIcon({
  className: '',
  html: `<div class="rp-dest-pin">
    <svg viewBox="0 0 24 24" fill="white" width="16" height="16">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  </div>`,
  iconSize: [42, 52],
  iconAnchor: [21, 52],
});

// ─── Haversine ETA ────────────────────────────────────────────────────────────
function calcEtaMinutos(lat1, lon1, lat2, lon2, kmhMedio = 35) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 6371;
  const toR = (v) => (v * Math.PI) / 180;
  const dLat = toR(lat2 - lat1);
  const dLon = toR(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toR(lat1)) * Math.cos(toR(lat2)) * Math.sin(dLon / 2) ** 2;
  const km = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.max(1, Math.round((km / kmhMedio) * 60));
}

// ─── Controlador de câmera suave ──────────────────────────────────────────────
function MapCameraController({ driverPos, destPos, initialFit }) {
  const map = useMap();
  const fittedRef = useRef(false);

  useEffect(() => {
    if (initialFit && !fittedRef.current && driverPos && destPos) {
      const bounds = L.latLngBounds([driverPos, destPos]);
      map.fitBounds(bounds, { padding: [80, 80] });
      fittedRef.current = true;
    }
  }, [driverPos, destPos, initialFit, map]);

  useEffect(() => {
    if (!driverPos || !fittedRef.current) return;
    map.panTo(driverPos, { animate: true, duration: 1.2 });
  }, [driverPos, map]);

  return null;
}

// ─── Estágios do atendimento ──────────────────────────────────────────────────
const STAGES = [
  { key: 'a_caminho',  label: 'A caminho',    icon: '🚛' },
  { key: 'chegando',   label: 'Chegando',      icon: '📍' },
  { key: 'no_local',   label: 'No local',      icon: '✅' },
];

function getStageIndex(etaMin) {
  if (etaMin === null) return 0;
  if (etaMin > 5) return 0;
  if (etaMin > 1) return 1;
  return 2;
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
export default function RastreioPublico() {
  const { token } = useParams();

  const [data,         setData]         = useState(null);
  const [driverPos,    setDriverPos]    = useState(null);
  const [error,        setError]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [socketOk,     setSocketOk]     = useState(false);
  const [lastUpdate,   setLastUpdate]   = useState(null);
  const [eta,          setEta]          = useState(null);
  const [destParsed,   setDestParsed]   = useState(null);
  const [sheetOpen,    setSheetOpen]    = useState(true);
  const [initialFit,   setInitialFit]   = useState(false);

  const socketRef  = useRef(null);
  const pollRef    = useRef(null);
  const markerRef  = useRef(null);

  // ── Fetch dados públicos ───────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!token) { setError('Token não fornecido.'); setLoading(false); return; }

    try {
      const res = await fetch(`${API_BASE}/api/ordens/rastreio-publico/${token}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Erro ${res.status}`);
      }
      const json = await res.json();
      setData(json);

      if (json.motorista_lat && json.motorista_lng) {
        const pos = [parseFloat(json.motorista_lat), parseFloat(json.motorista_lng)];
        setDriverPos(pos);
        setLastUpdate(Date.now());
        if (!initialFit) setInitialFit(true);
      }

      // Parse destino — tenta geocode simples ou usa coordenadas do OS se disponíveis
      if (json.dest_lat && json.dest_lng) {
        setDestParsed([parseFloat(json.dest_lat), parseFloat(json.dest_lng)]);
      }

      setError(null);
    } catch (err) {
      setError(err.message || 'Rastreamento indisponível.');
    } finally {
      setLoading(false);
    }
  }, [token, initialFit]);

  // ── ETA recalc sempre que driverPos mudar ──────────────────────────────────
  useEffect(() => {
    if (!driverPos || !destParsed) { setEta(null); return; }
    setEta(calcEtaMinutos(driverPos[0], driverPos[1], destParsed[0], destParsed[1]));
  }, [driverPos, destParsed]);

  // ── Socket.io — escuta locationUpdate sem auth ─────────────────────────────
  useEffect(() => {
    if (!data?.motorista_id) return;

    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      reconnectionAttempts: 8,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    socket.on('connect', () => setSocketOk(true));
    socket.on('disconnect', () => setSocketOk(false));

    socket.on('locationUpdate', (payload) => {
      if (String(payload.motoristaId) !== String(data.motorista_id)) return;
      if (payload.latitude == null || payload.longitude == null) return;

      const pos = [parseFloat(payload.latitude), parseFloat(payload.longitude)];
      setDriverPos(pos);
      setLastUpdate(Date.now());

      // Animação suave do marcador
      if (markerRef.current) {
        markerRef.current.setLatLng(pos);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [data?.motorista_id]);

  // ── Polling de fallback a cada 8s ──────────────────────────────────────────
  useEffect(() => {
    pollRef.current = setInterval(fetchData, 8000);
    return () => clearInterval(pollRef.current);
  }, [fetchData]);

  // ── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Formata tempo desde última atualização ─────────────────────────────────
  const [sinceTick, setSinceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSinceTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const sinceLabel = (() => {
    if (!lastUpdate) return null;
    const s = Math.floor((Date.now() - lastUpdate) / 1000);
    if (s < 5) return 'agora mesmo';
    if (s < 60) return `${s}s atrás`;
    return `${Math.floor(s / 60)}m atrás`;
  })();

  // ── Centraliza mapa sem reinicializar ─────────────────────────────────────
  const mapCenter = driverPos || [-22.9068, -47.0616];
  const stageIdx  = getStageIndex(eta);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER — LOADING
  // ─────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="rp-root">
        <div className="rp-loading-screen">
          <div className="rp-logo-mark">
            <span>🚛</span>
          </div>
          <div className="rp-spinner" />
          <p>Localizando seu atendimento…</p>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER — ERRO
  // ─────────────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="rp-root">
        <div className="rp-error-screen">
          <div className="rp-error-icon">⚠️</div>
          <h2>Rastreamento indisponível</h2>
          <p>{error}</p>
          <button onClick={fetchData} className="rp-retry-btn">
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const isFinalizado = ['Concluído', 'Finalizada', 'Cancelado'].includes(data?.status);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER — FINALIZADO
  // ─────────────────────────────────────────────────────────────────────────
  if (isFinalizado) {
    return (
      <div className="rp-root">
        <div className="rp-done-screen">
          <div className="rp-done-icon">
            {data.status === 'Cancelado' ? '✕' : '✓'}
          </div>
          <h2>{data.status === 'Cancelado' ? 'Atendimento cancelado' : 'Atendimento concluído'}</h2>
          <p>OS #{data.os_id}</p>
          <p className="rp-done-sub">Obrigado por utilizar nosso serviço!</p>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER — MAPA + BOTTOM SHEET
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="rp-root">

      {/* ── HEADER flutuante ── */}
      <header className="rp-header">
        <div className="rp-header-left">
          <div className="rp-brand">
            <span className="rp-brand-icon">🚛</span>
            <span className="rp-brand-name">Rastreio ao Vivo</span>
          </div>
          <span className="rp-os-chip">OS #{data?.os_id}</span>
        </div>
        <div className="rp-signal">
          <span className={`rp-signal-dot ${socketOk ? 'live' : 'poll'}`} />
          <span className="rp-signal-label">
            {socketOk ? 'Ao Vivo' : 'Atualizando…'}
          </span>
        </div>
      </header>

      {/* ── MAPA ── */}
      <div className="rp-map-wrapper">
        <MapContainer
          center={mapCenter}
          zoom={15}
          zoomControl={false}
          attributionControl={false}
          className="rp-map"
        >
          <TileLayer
            url={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
            tileSize={512}
            zoomOffset={-1}
            keepBuffer={4}
          />

          {/* Marcador do motorista */}
          {driverPos && (
            <Marker
              position={driverPos}
              icon={createDriverIcon('#F59E0B')}
              ref={markerRef}
            />
          )}

          {/* Marcador do destino */}
          {destParsed && (
            <Marker position={destParsed} icon={DEST_ICON} />
          )}

          <MapCameraController
            driverPos={driverPos}
            destPos={destParsed}
            initialFit={initialFit}
          />
        </MapContainer>

        {/* GPS tag flutuante no mapa */}
        {sinceLabel && (
          <div className="rp-gps-tag">
            <span className="rp-gps-dot" />
            GPS {sinceLabel}
          </div>
        )}
      </div>

      {/* ── BOTTOM SHEET ── */}
      <div className={`rp-sheet ${sheetOpen ? 'open' : 'collapsed'}`}>

        {/* Handle para arrastar */}
        <div className="rp-sheet-handle" onClick={() => setSheetOpen((v) => !v)}>
          <div className="rp-handle-bar" />
        </div>

        {/* ETA Hero */}
        <div className="rp-eta-hero">
          <div className="rp-eta-main">
            {eta !== null ? (
              <>
                <span className="rp-eta-num">{eta}</span>
                <span className="rp-eta-unit">min</span>
              </>
            ) : (
              <span className="rp-eta-num rp-eta-loading">—</span>
            )}
          </div>
          <div className="rp-eta-label">Tempo estimado de chegada</div>
        </div>

        {/* Progress stages */}
        <div className="rp-stages">
          {STAGES.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className={`rp-stage ${i <= stageIdx ? 'active' : ''} ${i === stageIdx ? 'current' : ''}`}>
                <div className="rp-stage-icon">{s.icon}</div>
                <div className="rp-stage-label">{s.label}</div>
              </div>
              {i < STAGES.length - 1 && (
                <div className={`rp-stage-line ${i < stageIdx ? 'filled' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Divider */}
        <div className="rp-divider" />

        {/* Driver card */}
        <div className="rp-driver-card">
          <div className="rp-driver-avatar">
            <span>{(data?.motorista_nome || 'M')[0].toUpperCase()}</span>
          </div>
          <div className="rp-driver-info">
            <span className="rp-driver-name">{data?.motorista_nome || '—'}</span>
            <span className="rp-driver-role">Operador de Guincho</span>
          </div>
          <div className="rp-driver-status">
            <div className="rp-driver-badge">
              <span className={`rp-badge-dot ${driverPos ? 'online' : ''}`} />
              {driverPos ? 'Online' : 'Offline'}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="rp-divider" />

        {/* Destino */}
        <div className="rp-dest-row">
          <div className="rp-dest-icon-wrap">
            <span>📍</span>
          </div>
          <div className="rp-dest-info">
            <span className="rp-dest-label">Local de atendimento</span>
            <span className="rp-dest-addr">{data?.local_atendimento || '—'}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="rp-sheet-footer">
          <span>Atualizado {sinceLabel || 'agora'}</span>
          <span>·</span>
          <span>{socketOk ? '🟢 Conexão ao vivo' : '🔄 Atualizando…'}</span>
        </div>
      </div>
    </div>
  );
}